import type { AgeValidationResult } from '@/features/enrollment'

/**
 * Validates the age when a gamer started playing video games
 * @param ageStartedGaming - The age when they started gaming (as string)
 * @param birthYear - Birth year (optional)
 * @param birthMonth - Birth month (optional)
 * @param birthDay - Birth day (optional)
 * @returns AgeValidationResult with validation details
 */
export function validateAgeStartedGaming(
  ageStartedGaming: string,
  birthYear?: string,
  birthMonth?: string,
  birthDay?: string
): AgeValidationResult {
  // Type validation - ensure it's a valid number
  const ageInput = ageStartedGaming.toString().trim()
  const age = parseInt(ageInput)
  
  // Check if it's a valid integer
  if (isNaN(age) || !Number.isInteger(parseFloat(ageInput))) {
    return {
      isValid: false,
      age: 0,
      errorMessage: 'Please enter a valid whole number for age'
    }
  }
  
  if (age < 1) {
    return {
      isValid: false,
      age,
      errorMessage: 'Age must be at least 1 year old'
    }
  }
  
  if (age > 100) {
    return {
      isValid: false,
      age,
      errorMessage: 'Age seems unrealistic (maximum 100 years)'
    }
  }
  
  // If birth date information is available, perform comprehensive validation
  if (birthYear && birthMonth && birthDay) {
    const birthYearNum = parseInt(birthYear)
    const birthMonthNum = parseInt(birthMonth)
    const birthDayNum = parseInt(birthDay)
    
    // Validate birth date components
    if (isNaN(birthYearNum) || isNaN(birthMonthNum) || isNaN(birthDayNum)) {
      return {
        isValid: false,
        age,
        errorMessage: 'Invalid birth date information'
      }
    }
    
    const birthDate = new Date(birthYearNum, birthMonthNum - 1, birthDayNum)
    
    // Check if birth date is valid
    if (birthDate.getFullYear() !== birthYearNum || 
        birthDate.getMonth() !== birthMonthNum - 1 || 
        birthDate.getDate() !== birthDayNum) {
      return {
        isValid: false,
        age,
        errorMessage: 'Invalid birth date'
      }
    }
    
    const today = new Date()
    const currentAge = Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    
    // Validate age started gaming against current age
    if (age > currentAge) {
      return {
        isValid: false,
        age,
        currentAge,
        errorMessage: `Cannot be older than current age (${currentAge} years old)`
      }
    }
    
    // Calculate and validate the year they started gaming
    const firstGamingYear = birthYearNum + age
    const currentYear = new Date().getFullYear()
    
    if (firstGamingYear > currentYear) {
      return {
        isValid: false,
        age,
        currentAge,
        firstGamingYear,
        errorMessage: `This would result in starting gaming in ${firstGamingYear} (future date)`
      }
    }
    
    if (firstGamingYear < 1975) {
      return {
        isValid: false,
        age,
        currentAge,
        firstGamingYear,
        errorMessage: `This would result in starting gaming in ${firstGamingYear} (before video games were widely available)`
      }
    }
    
    if (firstGamingYear < 1980 && age < 5) {
      return {
        isValid: false,
        age,
        currentAge,
        firstGamingYear,
        errorMessage: `Starting gaming at age ${age} in ${firstGamingYear} seems unrealistic for that era`
      }
    }
    
    if (age < 2) {
      return {
        isValid: false,
        age,
        currentAge,
        firstGamingYear,
        errorMessage: 'Age seems too young for gaming (minimum 2 years old)'
      }
    }
    
    if (age > 90) {
      return {
        isValid: false,
        age,
        currentAge,
        firstGamingYear,
        errorMessage: 'Age seems unrealistic for starting gaming (maximum 90 years)'
      }
    }
    
    // All validations passed
    return {
      isValid: true,
      age,
      currentAge,
      firstGamingYear
    }
  }
  
  // Fallback validation when birth date is not available
  if (age < 2) {
    return {
      isValid: false,
      age,
      errorMessage: 'Age seems too young for gaming (minimum 2 years old)'
    }
  }
  
  if (age > 90) {
    return {
      isValid: false,
      age,
      errorMessage: 'Age seems unrealistic for starting gaming (maximum 90 years)'
    }
  }
  
  // Basic validation passed
  return {
    isValid: true,
    age
  }
}

/**
 * Formats age validation error messages for display
 * @param validationResult - The result from validateAgeStartedGaming
 * @returns Formatted error message or empty string if valid
 */
export function getAgeValidationErrorMessage(validationResult: AgeValidationResult): string {
  return validationResult.errorMessage || ''
}

/**
 * Checks if the input value is a valid whole number
 * @param value - The input value to validate
 * @returns True if the value is a valid whole number
 */
export function isValidWholeNumber(value: string): boolean {
  return value === '' || /^\d+$/.test(value)
}

/**
 * Normalizes age input to ensure it's a valid whole number
 * @param value - The input value to normalize
 * @returns Normalized age value as string
 */
export function normalizeAgeInput(value: string): string {
  if (!value) return ''
  
  const num = parseFloat(value)
  if (isNaN(num)) return ''
  
  return Math.floor(num).toString()
}

/**
 * Calculates current age from birth date components
 * @param birthYear - Birth year
 * @param birthMonth - Birth month
 * @param birthDay - Birth day
 * @returns Current age in years
 */
export function calculateCurrentAge(birthYear: string, birthMonth: string, birthDay: string): number | null {
  const birthYearNum = parseInt(birthYear)
  const birthMonthNum = parseInt(birthMonth)
  const birthDayNum = parseInt(birthDay)
  
  if (isNaN(birthYearNum) || isNaN(birthMonthNum) || isNaN(birthDayNum)) {
    return null
  }
  
  const birthDate = new Date(birthYearNum, birthMonthNum - 1, birthDayNum)
  
  // Check if birth date is valid
  if (birthDate.getFullYear() !== birthYearNum || 
      birthDate.getMonth() !== birthMonthNum - 1 || 
      birthDate.getDate() !== birthDayNum) {
    return null
  }
  
  const today = new Date()
  return Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
}

/**
 * Calculates current age from a date string (YYYY-MM-DD format)
 * @param dobString - Date of birth string in YYYY-MM-DD format
 * @returns Current age in years or null if invalid
 */
export function calculateAgeFromDOB(dobString: string): number | null {
  if (!dobString) return null
  
  const birthDate = new Date(dobString)
  
  // Check if the date is valid
  if (isNaN(birthDate.getTime())) {
    return null
  }
  
  const today = new Date()
  return Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
}

/**
 * Validates DOB based on relationship to victim
 * @param dobString - Date of birth string in YYYY-MM-DD format
 * @param relationToVictim - Relationship to victim ("Myself" or "Loved one")
 * @param campaign - Optional campaign object with minAge/maxAge settings
 * @returns Validation result with error message if invalid
 *
 * Note: Age-based eligibility (minAge/maxAge) is now handled by campaign-specific validation.
 * This function only validates DOB format and basic logic.
 */
export function validateDOBByRelationship(
  dobString: string,
  relationToVictim: string,
  campaign?: { minAge?: number | null; maxAge?: number | null }
): { isValid: boolean; errorMessage?: string } {
  if (!dobString) {
    return { isValid: false, errorMessage: 'Date of birth is required' }
  }

  const age = calculateAgeFromDOB(dobString)

  if (age === null) {
    return { isValid: false, errorMessage: 'Invalid date of birth format' }
  }

  // ✅ Campaign-specific age validation (if campaign settings provided)
  if (campaign) {
    const minAge = campaign.minAge ?? null
    const maxAge = campaign.maxAge ?? null

    // Check if age exceeds maximum
    if (maxAge !== null && age > maxAge) {
      return {
        isValid: false,
        errorMessage: `DQ: Must be ${maxAge} or younger for this campaign`
      }
    }

    // Check if age is below minimum
    if (minAge !== null && age < minAge) {
      return {
        isValid: false,
        errorMessage: `DQ: Must be at least ${minAge} years old for this campaign`
      }
    }
  }

  return { isValid: true }
}

/**
 * Calculates age at the time of a specific date
 * @param dobString - Date of birth string in YYYY-MM-DD format
 * @param incidentDateString - Incident date string in YYYY-MM-DD format
 * @returns Age in years at the time of incident or null if invalid (e.g., incident before birth)
 */
export function calculateAgeAtIncident(dobString: string, incidentDateString: string): number | null {
  if (!dobString || !incidentDateString) return null
  
  const birthDate = new Date(dobString)
  const incidentDate = new Date(incidentDateString)
  
  // Check if the dates are valid
  if (isNaN(birthDate.getTime()) || isNaN(incidentDate.getTime())) {
    return null
  }
  
  // Check if incident date is before birth date (invalid scenario)
  if (incidentDate < birthDate) {
    return null // Return null to indicate invalid calculation (would be negative age)
  }
  
  // Calculate age at incident
  let age = incidentDate.getFullYear() - birthDate.getFullYear()
  const monthDiff = incidentDate.getMonth() - birthDate.getMonth()
  
  // Adjust if birthday hasn't occurred yet in the incident year
  if (monthDiff < 0 || (monthDiff === 0 && incidentDate.getDate() < birthDate.getDate())) {
    age--
  }
  
  // Final check: age should never be negative (double check)
  if (age < 0) {
    return null
  }
  
  return age
}

/**
 * Validates exploitation age and date logic based on whether victim is adult or minor
 * @param exploitationStartDate - Date string in YYYY-MM-DD format when exploitation started
 * @param injuredPartyDOB - Date of birth string in YYYY-MM-DD format
 * @param injuredPartyAgeAtExploitation - Age at time of exploitation (can be calculated)
 * @returns Validation result with error message if invalid
 */
export function validateExploitationAgeAndDate(
  exploitationStartDate: string,
  injuredPartyDOB: string,
  injuredPartyAgeAtExploitation?: number
): { isValid: boolean; errorMessage?: string; calculatedAge?: number } {
  if (!exploitationStartDate) {
    return { isValid: false, errorMessage: 'Exploitation start date is required' }
  }
  
  if (!injuredPartyDOB) {
    return { isValid: false, errorMessage: 'Date of birth is required for validation' }
  }
  
  const startDate = new Date(exploitationStartDate)
  const today = new Date()
  const birthDate = new Date(injuredPartyDOB)
  
  // Check if the dates are valid
  if (isNaN(startDate.getTime()) || isNaN(birthDate.getTime())) {
    return { isValid: false, errorMessage: 'Invalid date format' }
  }
  
  // Calculate current age (using TODAY_DATE)
  const currentAge = calculateAgeFromDOB(injuredPartyDOB)
  if (currentAge === null) {
    return { isValid: false, errorMessage: 'Invalid date of birth' }
  }
  
  // Check if incident date is before birth date (invalid scenario)
  if (startDate < birthDate) {
    return { 
      isValid: false, 
      errorMessage: 'DQ: Invalid date - incident date cannot be before date of birth' 
    }
  }
  
  // Calculate age at time of incident (when event happened)
  const ageAtIncident = injuredPartyAgeAtExploitation !== undefined 
    ? injuredPartyAgeAtExploitation 
    : calculateAgeAtIncident(injuredPartyDOB, exploitationStartDate)
  
  if (ageAtIncident === null) {
    return { isValid: false, errorMessage: 'DQ: Invalid date - incident date cannot be before date of birth' }
  }
  
  // Additional check: age should never be negative
  if (ageAtIncident < 0) {
    return { 
      isValid: false, 
      errorMessage: 'DQ: Invalid date - incident date cannot be before date of birth',
      calculatedAge: ageAtIncident
    }
  }
  
  // Calculate time differences (using TODAY_DATE)
  const threeWeeksAgo = new Date(today.getTime() - (3 * 7 * 24 * 60 * 60 * 1000))
  const twoYearsAgo = new Date(today.getTime() - (2 * 365 * 24 * 60 * 60 * 1000))
  
  // Check if date is less than 3 weeks ago
  if (startDate > threeWeeksAgo) {
    return { 
      isValid: false, 
      errorMessage: 'DQ: Event happened too recently - exploitation must have started at least 3 weeks ago',
      calculatedAge: ageAtIncident
    }
  }
  
  // Determine if victim is currently an adult (18 or older) - comparing TODAY_DATE and DOB
  const isAdult = currentAge >= 18
  
  if (isAdult) {
    // Adult case: Must have been older than 16 and younger than 18 at time of incident
    // AND incident must have happened within 2 years ago (comparing when event happened to TODAY_DATE)
    
    // Check if incident happened too long ago (comparing when event happened to TODAY_DATE)
    if (startDate < twoYearsAgo) {
      return { 
        isValid: false, 
        errorMessage: 'DQ: Event happened too long ago - for adults, exploitation must have started within the last 2 years',
        calculatedAge: ageAtIncident
      }
    }
    
    // Check age at time of incident for adults (must be >16 and <18)
    if (ageAtIncident <= 16) {
      return { 
        isValid: false, 
        errorMessage: 'DQ: Too young - for adults, victim must have been older than 16 at time of incident',
        calculatedAge: ageAtIncident
      }
    }
    
    if (ageAtIncident >= 18) {
      return { 
        isValid: false, 
        errorMessage: 'DQ: Too old - for adults, victim must have been younger than 18 at time of incident',
        calculatedAge: ageAtIncident
      }
    }
  } else {
    // Minor case: Must have been older than 3 and younger than 18 at time of incident
    // Check if incident happened too long ago (more than 2 years)
    if (startDate < twoYearsAgo) {
      // For minors, check how old they were at the actual incident date
      // If they were 18 or older at the incident date, DQ
      if (ageAtIncident >= 18) {
        return { 
          isValid: false, 
          errorMessage: 'DQ: Event happened too long ago - victim was 18 or older at time of incident',
          calculatedAge: ageAtIncident
        }
      }
    }
    
    // Check age at time of incident for minors (must be >3 and <18)
    if (ageAtIncident <= 3) {
      return { 
        isValid: false, 
        errorMessage: 'DQ: Too young - victim must have been older than 3 at time of incident',
        calculatedAge: ageAtIncident
      }
    }
    
    if (ageAtIncident >= 18) {
      return { 
        isValid: false, 
        errorMessage: 'DQ: Too old - victim must have been younger than 18 at time of incident',
        calculatedAge: ageAtIncident
      }
    }
  }
  
  // All validations passed
  return { isValid: true, calculatedAge: ageAtIncident }
}

/**
 * Validates exploitation start date to ensure it's within acceptable timeframe
 * @param exploitationStartDate - Date string in YYYY-MM-DD format
 * @returns Validation result with error message if invalid
 */
export function validateExploitationStartDate(exploitationStartDate: string): { isValid: boolean; errorMessage?: string } {
  if (!exploitationStartDate) {
    return { isValid: false, errorMessage: 'Exploitation start date is required' }
  }
  
  const startDate = new Date(exploitationStartDate)
  const today = new Date()
  
  // Check if the date is valid
  if (isNaN(startDate.getTime())) {
    return { isValid: false, errorMessage: 'Invalid date format' }
  }
  
  // Calculate time differences in milliseconds
  const threeWeeksAgo = new Date(today.getTime() - (3 * 7 * 24 * 60 * 60 * 1000))
  const twoYearsAgo = new Date(today.getTime() - (2 * 365 * 24 * 60 * 60 * 1000))
  
  // Check if date is less than 3 weeks ago
  if (startDate > threeWeeksAgo) {
    return { isValid: false, errorMessage: 'DQ: Exploitation must have started at least 3 weeks ago' }
  }
  
  // Check if date is more than 2 years ago
  if (startDate < twoYearsAgo) {
    return { isValid: false, errorMessage: 'DQ: Exploitation must have started within the last 2 years' }
  }
  
  return { isValid: true }
}
